import React, { useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Boxes,
  Clock3,
  Hash,
  Layers,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";

import { DashboardLayout } from "@/components/layout";
import { ContextBar } from "@/components/catalog";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  PageHeader,
} from "@/components/ui";
import { useMasterSkus, type MasterSkuItem } from "@/hooks/useMasterSkus";
import { formatTimestamp } from "@/lib/catalog/formatTimestamp";
import { cn } from "@/lib/utils";

type SortKey = "skuCode" | "name" | "version" | "updatedAt";
type SortDirection = "asc" | "desc";

const SORT_LABELS: Record<SortKey, string> = {
  skuCode: "SKU Code",
  name: "Name",
  version: "Version",
  updatedAt: "Updated",
};

function getSkuSearchText(sku: MasterSkuItem): string {
  return [sku.skuCode, sku.name, sku.description ?? "", String(sku.version)]
    .join(" ")
    .toLowerCase();
}

function getSortValue(sku: MasterSkuItem, sortKey: SortKey): string | number {
  if (sortKey === "version") return sku.version;
  if (sortKey === "updatedAt") return Date.parse(sku.updatedAt) || 0;
  return sku[sortKey].toLowerCase();
}

function sortMasterSkus(
  skus: MasterSkuItem[],
  sortKey: SortKey,
  sortDirection: SortDirection,
): MasterSkuItem[] {
  return [...skus].sort((a, b) => {
    const aValue = getSortValue(a, sortKey);
    const bValue = getSortValue(b, sortKey);
    const direction = sortDirection === "asc" ? 1 : -1;

    if (typeof aValue === "number" && typeof bValue === "number") {
      return (aValue - bValue) * direction;
    }

    return String(aValue).localeCompare(String(bValue)) * direction;
  });
}

function getLatestUpdatedAt(skus: MasterSkuItem[]): string | null {
  return skus.reduce<string | null>((latest, sku) => {
    if (!latest) return sku.updatedAt;
    return Date.parse(sku.updatedAt) > Date.parse(latest)
      ? sku.updatedAt
      : latest;
  }, null);
}

export default function MasterSKUs() {
  const { skus, totalCount, loading, error, workspaceName, refetch } =
    useMasterSkus();
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const filteredSkus = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matchingSkus = query
      ? skus.filter((sku) => getSkuSearchText(sku).includes(query))
      : skus;

    return sortMasterSkus(matchingSkus, sortKey, sortDirection);
  }, [search, skus, sortDirection, sortKey]);

  const latestUpdatedAt = useMemo(() => getLatestUpdatedAt(skus), [skus]);

  const handleSort = (nextSortKey: SortKey) => {
    if (sortKey === nextSortKey) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortKey(nextSortKey);
    setSortDirection(nextSortKey === "updatedAt" ? "desc" : "asc");
  };

  const hasSearch = search.trim().length > 0;
  const showErrorState = Boolean(error && !loading && skus.length === 0);

  return (
    <DashboardLayout activePath="/catalog/master-skus">
      <PageHeader
        title="Master SKUs"
        subtitle={
          workspaceName
            ? `Manage unified product identifiers for ${workspaceName}`
            : "Manage unified product identifiers across marketplaces"
        }
        breadcrumbs={[
          { label: "Catalog", active: false },
          { label: "Master SKUs", active: true },
        ]}
        actions={
          <Button
            data-testid="master-skus-refresh"
            variant="outline"
            onClick={refetch}
            disabled={loading}
          >
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            Refresh
          </Button>
        }
      />

      <ContextBar />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Master SKUs
              </p>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {totalCount.toLocaleString("en-IN")}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Showing
              </p>
              <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {filteredSkus.length.toLocaleString("en-IN")}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-sky-50 dark:bg-sky-950 text-sky-600 dark:text-sky-400">
              <Clock3 className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Last Updated
              </p>
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {formatTimestamp(latestUpdatedAt)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="text-base">Master SKU Directory</CardTitle>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {totalCount.toLocaleString("en-IN")} records from the catalog API
            </p>
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="master-skus-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search SKU, name, description"
              className="pl-9"
            />
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {error && (
            <div className="mx-6 mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">
              {error}
            </div>
          )}

          {loading ? (
            <div
              data-testid="master-skus-loading"
              className="flex items-center justify-center gap-3 px-6 py-12 text-sm text-slate-500 dark:text-slate-400"
            >
              <Loader2 className="w-5 h-5 animate-spin text-indigo-500" />
              Loading master SKUs...
            </div>
          ) : showErrorState ? (
            <EmptyState
              icon={AlertCircle}
              title="Could not load master SKUs"
              description="The catalog API request failed. Refresh the directory to try again."
              actionLabel="Retry"
              onAction={refetch}
            />
          ) : filteredSkus.length > 0 ? (
            <div className="w-full overflow-auto">
              <table className="w-full min-w-[760px] border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/50">
                    {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => {
                      const isActive = sortKey === key;
                      const SortIcon =
                        sortDirection === "asc" ? ArrowUp : ArrowDown;

                      return (
                        <th
                          key={key}
                          className="px-6 py-3 text-left text-xs font-semibold uppercase text-slate-500 dark:text-slate-400"
                        >
                          <button
                            type="button"
                            data-testid={`master-skus-sort-${key}`}
                            onClick={() => handleSort(key)}
                            className="inline-flex items-center gap-1 rounded-sm hover:text-slate-700 dark:hover:text-slate-200"
                          >
                            {SORT_LABELS[key]}
                            {isActive && <SortIcon className="w-3.5 h-3.5" />}
                          </button>
                        </th>
                      );
                    })}
                    <th className="px-6 py-3 text-left text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSkus.map((sku) => (
                    <tr
                      key={sku.id}
                      data-testid={`master-sku-row-${sku.id}`}
                      className="border-b border-slate-100 bg-white transition-colors hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800/50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Hash className="w-4 h-4 text-indigo-500" />
                          <span className="font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                            {sku.skuCode}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-sm">
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {sku.name}
                          </p>
                          <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                            {sku.description || "No description"}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex min-w-12 items-center justify-center rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                          v{sku.version}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                        {formatTimestamp(sku.updatedAt)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300">
                        {formatTimestamp(sku.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={Boxes}
              title={hasSearch ? "No matching master SKUs" : "No master SKUs"}
              description={
                hasSearch
                  ? "Try a different SKU code, product name, or description."
                  : "Master SKUs will appear here after catalog ingestion creates unified product identifiers."
              }
              actionLabel={hasSearch ? "Clear Search" : undefined}
              onAction={hasSearch ? () => setSearch("") : undefined}
            />
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
